import { jsPDF } from 'jspdf';

/* ── date helpers ── */
function toLocal(iso) {
  if (!iso) return new Date(0);
  const [y, m, d] = iso.split('T')[0].split('-').map(Number);
  return new Date(y, m - 1, d);
}
function daysBetween(a, b) { return Math.round((b - a) / 86400000); }
function addDays(date, n) { const d = new Date(date); d.setDate(d.getDate() + n); return d; }

/* ── lane packing ── */
function assignLanes(ms) {
  const sorted = [...ms]
    .filter(m => m.startDate && m.endDate)
    .sort((a, b) => toLocal(a.startDate) - toLocal(b.startDate));
  const lanes = [];
  const laneMap = {};
  for (const m of sorted) {
    let placed = false;
    for (let i = 0; i < lanes.length; i++) {
      const last = lanes[i][lanes[i].length - 1];
      if (toLocal(m.startDate) > toLocal(last.endDate)) {
        lanes[i].push(m); laneMap[m.id] = i; placed = true; break;
      }
    }
    if (!placed) { laneMap[m.id] = lanes.length; lanes.push([m]); }
  }
  return { laneMap, numLanes: Math.max(1, lanes.length) };
}

/* ── status colors (light/print-safe) ── */
const STATUS = {
  planned:       { bg: [239, 246, 255], border: [147, 197, 253], text: [30,  64,  175], label: 'Planned'     },
  'in-progress': { bg: [255, 251, 235], border: [252, 211, 77],  text: [146, 64,  14],  label: 'In Progress' },
  done:          { bg: [240, 253, 244], border: [134, 239, 172], text: [22,  101, 52],   label: 'Done'        },
};
const DEFAULT_STATUS = STATUS.planned;

/* ── layout constants (PDF points, A4 landscape = 841.89 × 595.28) ── */
const PW       = 841.89;
const PH       = 595.28;
const MG       = 22;       // page margin
const LEFT_W   = 155;      // left label column
const HDR_H    = 36;       // title bar
const MONTH_H  = 16;       // month label row
const DAY_H    = 18;       // day/week label row
const LEG_H    = 26;       // bottom legend
const LANE_H   = 32;       // height per milestone lane
const ROW_PAD  = 7;        // padding above/below lanes
const BAR_H    = 20;       // milestone bar height
const INIT_H   = 20;       // goal section header

const TL_X     = MG + LEFT_W;                    // timeline area start x
const TL_W     = PW - MG - LEFT_W - MG;          // timeline area width
const CT_TOP   = MG + HDR_H + MONTH_H + DAY_H;   // first content row y
const CT_BOT   = PH - MG - LEG_H;                // last content row y

/* ── colors ── */
const C = {
  white:   [255, 255, 255],
  bg:      [250, 251, 253],
  surface: [241, 245, 249],
  text:    [15,  23,  42],
  muted:   [100, 116, 139],
  border:  [203, 213, 225],
  light:   [226, 232, 240],
  red:     [239, 68,  68],
  blue:    [59,  130, 246],
};

const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

/* ─────────────────────────────────────────────────────── */
/*  Main export function                                   */
/*  groupedRows  – same shape as MilestoneTimeline state  */
/*  selectedProjectIds – Set<number> | null (null = all)  */
/*  exportStart / exportEnd – Date objects                */
/* ─────────────────────────────────────────────────────── */
export function exportTimelinePDF({ groupedRows, selectedProjectIds, exportStart, exportEnd }) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  doc.setLineWidth(0.5);

  const totalDays = daysBetween(exportStart, exportEnd) + 1;
  const useWeeks  = totalDays > 90;
  const numCols   = useWeeks ? Math.ceil(totalDays / 7) : totalDays;
  const COL_W     = TL_W / numCols;

  /* ── coordinate helpers ── */
  const dateToX = (iso) => {
    const days = daysBetween(exportStart, toLocal(iso));
    return TL_X + (useWeeks ? (days / 7) * COL_W : days * COL_W);
  };
  const spanW = (s, e) => {
    const days = Math.max(1, daysBetween(toLocal(s), toLocal(e)) + 1);
    return Math.max(COL_W * 0.5, useWeeks ? (days / 7) * COL_W : days * COL_W);
  };

  /* ── month spans ── */
  const monthMap = new Map();
  for (let i = 0; i < totalDays; i++) {
    const d   = addDays(exportStart, i);
    const key = `${d.getFullYear()}-${d.getMonth()}`;
    const col = useWeeks ? Math.floor(i / 7) : i;
    const lbl = `${MONTHS_SHORT[d.getMonth()].toUpperCase()} '${String(d.getFullYear()).slice(2)}`;
    if (!monthMap.has(key)) monthMap.set(key, { label: lbl, first: col, last: col });
    else monthMap.get(key).last = col;
  }
  const monthSpans = [...monthMap.values()];

  /* ── column labels (day or week) ── */
  const DAY_LETTERS = ['S','M','T','W','T','F','S'];
  const todayOffset = daysBetween(exportStart, new Date());
  const colLabels = useWeeks
    ? Array.from({ length: numCols }, (_, w) => {
        const d = addDays(exportStart, w * 7);
        return { col: w, label: `${d.getDate()}/${d.getMonth() + 1}`, weekend: false, today: false };
      })
    : Array.from({ length: totalDays }, (_, i) => {
        const d = addDays(exportStart, i);
        const dow = d.getDay();
        return { col: i, label: DAY_LETTERS[dow], weekend: dow === 0 || dow === 6, today: i === todayOffset };
      });

  /* ── filter groups by selected projects ── */
  const groups = groupedRows
    .map(g => ({
      goal: g.goal,
      rows: g.rows.filter(r => !selectedProjectIds || selectedProjectIds.has(r.project.id)),
    }))
    .filter(g => g.rows.length > 0);

  /* ── today x position (null if outside range) ── */
  const todayX = (todayOffset >= 0 && todayOffset < totalDays)
    ? TL_X + (useWeeks ? (todayOffset / 7) * COL_W : todayOffset * COL_W)
    : null;

  /* ── draw primitives ── */
  const fill  = (color)         => doc.setFillColor(...color);
  const stroke= (color)         => doc.setDrawColor(...color);
  const tcolor= (color)         => doc.setTextColor(...color);
  const frect = (x,y,w,h,c)    => { fill(c); doc.rect(x,y,w,h,'F'); };
  const fsrect= (x,y,w,h,fc,sc,lw=0.5) => { fill(fc); stroke(sc); doc.setLineWidth(lw); doc.rect(x,y,w,h,'FD'); doc.setLineWidth(0.5); };
  const hline = (x1,x2,y,c)    => { stroke(c||C.border); doc.line(x1,y,x2,y); };
  const vline = (x,y1,y2,c)    => { stroke(c||C.border); doc.line(x,y1,x,y2); };

  const trunc = (str, maxW) => {
    if (!str) return '';
    if (doc.getTextWidth(str) <= maxW) return str;
    let s = str;
    while (s.length > 0 && doc.getTextWidth(s + '…') > maxW) s = s.slice(0, -1);
    return s + '…';
  };

  /* ── page state ── */
  let pageNum   = 0;
  let contentY  = CT_TOP;
  let pageSectY = CT_TOP;   // y where current page's content rows start (for today line)

  /* ── draw page header + column headers ── */
  const drawPageHeader = () => {
    pageNum++;

    // Title bar
    frect(MG, MG, PW - MG*2, HDR_H, C.text);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    tcolor(C.white);
    doc.text('Phokii — Milestone Timeline', MG + 12, MG + HDR_H/2 + 4);

    // Date range (center)
    const fmt = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const rangeStr = `${fmt(exportStart)}  –  ${fmt(exportEnd)}`;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    tcolor([160, 174, 192]);
    doc.text(rangeStr, PW/2 - doc.getTextWidth(rangeStr)/2, MG + HDR_H/2 + 4);

    // Exported date (right)
    const expStr = `Exported ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    doc.setFontSize(7);
    doc.text(expStr, PW - MG - doc.getTextWidth(expStr) - 10, MG + HDR_H/2 + 4);

    // Month header row
    const mnY = MG + HDR_H;
    frect(MG, mnY, PW - MG*2, MONTH_H, C.bg);
    frect(MG, mnY, LEFT_W, MONTH_H, C.surface);
    vline(TL_X, mnY, mnY + MONTH_H);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6.5);
    tcolor(C.muted);
    monthSpans.forEach(span => {
      const x = TL_X + span.first * COL_W;
      vline(x, mnY, mnY + MONTH_H, C.light);
      doc.text(span.label, x + 4, mnY + MONTH_H - 4);
    });
    vline(PW - MG, mnY, mnY + MONTH_H, C.light);
    hline(MG, PW - MG, mnY + MONTH_H);

    // Day/week header row
    const dayY = mnY + MONTH_H;
    frect(MG, dayY, PW - MG*2, DAY_H, C.bg);
    frect(MG, dayY, LEFT_W, DAY_H, C.surface);
    vline(TL_X, dayY, dayY + DAY_H);
    doc.setFont('helvetica', useWeeks ? 'normal' : 'bold');
    doc.setFontSize(6);
    colLabels.forEach(({ col, label, weekend, today }) => {
      const x = TL_X + col * COL_W;
      if (weekend) frect(x, dayY, COL_W, DAY_H, C.surface);
      if (today)   frect(x, dayY, COL_W, DAY_H, [219, 234, 254]);
      vline(x, dayY, dayY + DAY_H, C.light);
      tcolor(today ? C.blue : weekend ? C.light : C.muted);
      doc.text(label, x + COL_W/2, dayY + DAY_H - 4, { align: 'center' });
    });
    vline(PW - MG, dayY, dayY + DAY_H, C.light);
    hline(MG, PW - MG, dayY + DAY_H);

    contentY  = CT_TOP;
    pageSectY = CT_TOP;
  };

  /* ── draw bottom legend ── */
  const drawLegend = () => {
    const ly = PH - MG - LEG_H;
    hline(MG, PW - MG, ly, C.light);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    tcolor(C.muted);
    doc.text('STATUS:', MG, ly + 16);
    let lx = MG + 46;
    Object.values(STATUS).forEach(s => {
      fsrect(lx, ly + 7, 10, 10, s.bg, s.border, 1.2);
      doc.setFont('helvetica', 'normal');
      tcolor(C.text);
      doc.text(s.label, lx + 13, ly + 16);
      lx += doc.getTextWidth(s.label) + 22;
    });
    if (todayX !== null) {
      stroke(C.red);
      doc.setLineWidth(1.5);
      doc.setLineDash([2, 2], 0);
      doc.line(lx + 2, ly + 12, lx + 16, ly + 12);
      doc.setLineDash([], 0);
      doc.setLineWidth(0.5);
      tcolor(C.text);
      doc.text('Today', lx + 19, ly + 16);
    }
    // Page number
    doc.setFontSize(7);
    tcolor(C.muted);
    const pgStr = `Page ${pageNum}`;
    doc.text(pgStr, PW - MG - doc.getTextWidth(pgStr), ly + 16);
  };

  /* ── draw today dashed line for current page ── */
  const drawTodayLine = (fromY, toY) => {
    if (todayX === null || toY <= fromY) return;
    stroke(C.red);
    doc.setLineWidth(1.2);
    doc.setLineDash([2, 3], 0);
    doc.line(todayX, fromY, todayX, toY);
    doc.setLineDash([], 0);
    doc.setLineWidth(0.5);
  };

  /* ── ensure there's vertical space, or start new page ── */
  const ensureSpace = (needed) => {
    if (contentY + needed > CT_BOT) {
      drawTodayLine(pageSectY, contentY);
      drawLegend();
      doc.addPage();
      drawPageHeader();
    }
  };

  /* ── draw one project row, returns row height ── */
  const drawRow = (projectRow, y) => {
    const { project, milestones: pMs } = projectRow;
    const inRange = pMs.filter(m =>
      m.startDate && m.endDate &&
      toLocal(m.endDate) >= exportStart && toLocal(m.startDate) <= exportEnd
    );
    const { laneMap, numLanes } = assignLanes(inRange);
    const rH = ROW_PAD * 2 + numLanes * LANE_H;

    // Row background
    frect(MG, y, PW - MG*2, rH, C.white);

    // Left column content
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    tcolor(C.text);
    doc.text(trunc(project.name, LEFT_W - 18), MG + 12, y + ROW_PAD + 11);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    tcolor(C.muted);
    doc.text(`${inRange.length} milestone${inRange.length !== 1 ? 's' : ''}`, MG + 12, y + ROW_PAD + 21);

    // Column divider + row border
    vline(TL_X, y, y + rH);
    hline(MG, PW - MG, y + rH, C.light);
    vline(PW - MG, y, y + rH, C.light);

    // Weekend shading + weekly grid lines
    colLabels.forEach(({ col, weekend }) => {
      if (weekend) frect(TL_X + col * COL_W, y, COL_W, rH, [247, 249, 252]);
    });
    for (let i = 0; i < numCols; i += (useWeeks ? 1 : 7)) {
      vline(TL_X + i * COL_W, y, y + rH, C.light);
    }

    // Milestone bars
    inRange.forEach(m => {
      const x0  = dateToX(m.startDate);
      const x   = Math.max(TL_X, x0);
      const w   = Math.min(spanW(m.startDate, m.endDate) - (x - x0), PW - MG - x);
      if (w <= 1) return;

      const lane = laneMap[m.id] ?? 0;
      const barY = y + ROW_PAD + lane * LANE_H + (LANE_H - BAR_H) / 2;
      const s    = STATUS[m.status] || DEFAULT_STATUS;

      fsrect(x, barY, w, BAR_H, s.bg, s.border, 1.5);

      const labelMaxW = w - 10;
      if (labelMaxW > 6) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7.5);
        tcolor(s.text);
        doc.text(trunc(m.name, labelMaxW), x + 5, barY + BAR_H/2 + 3);
      }

      // Duration label below bar when there's vertical room
      if (m.description && LANE_H - BAR_H > 8 && w > 50) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(6.5);
        tcolor(C.muted);
        doc.text(trunc(m.description, w - 8), x + 4, barY + BAR_H + 7);
      }
    });

    return rH;
  };

  /* ── main render ── */
  drawPageHeader();

  groups.forEach(({ goal, rows }) => {
    // Goal section header
    if (goal) {
      ensureSpace(INIT_H + ROW_PAD * 2 + LANE_H + 4);
      frect(MG, contentY, PW - MG*2, INIT_H, C.surface);
      frect(MG, contentY, LEFT_W, INIT_H, C.surface);
      vline(TL_X, contentY, contentY + INIT_H);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      tcolor(C.muted);
      doc.text(
        trunc(goal.name.toUpperCase(), LEFT_W - 16),
        MG + 12, contentY + INIT_H/2 + 3
      );
      hline(MG, PW - MG, contentY + INIT_H);
      contentY += INIT_H;
    }

    rows.forEach(projectRow => {
      const inRangeMs = projectRow.milestones.filter(m => m.startDate && m.endDate);
      const { numLanes } = assignLanes(inRangeMs);
      const rH = ROW_PAD * 2 + numLanes * LANE_H;
      ensureSpace(rH);
      contentY += drawRow(projectRow, contentY);
    });
  });

  // Final today line + legend on last page
  drawTodayLine(pageSectY, contentY);
  drawLegend();

  const dateTag = exportStart.toISOString().slice(0, 10);
  doc.save(`phokii-milestones-${dateTag}.pdf`);
}
